/**
 * Pure helpers for catalogue lot prev/next and current-lot resolution.
 * Kept free of React so navigation indexing can be unit-tested.
 */

export type SaleLotNavItem = {
  animalId?: number | null;
  lotNo: string;
  lotAnimalId?: number | null;
  lotAnimalName?: string | null;
  animalName?: string | null;
  askingPrice?: number | null;
  salePrice?: number | null;
  askingPriceDesc?: string | null;
  salePriceDesc?: string | null;
  vendorsId?: number | null;
  vendorsName?: string | null;
  currentOwnerId?: number | null;
  currentOwnerName?: string | null;
  lotNotes?: string | null;
  sale?: { saleName?: string | null } | null;
};

export type SaleLotNavTarget = {
  animalId: number | null;
  lotNo: string;
  lotAnimalId?: number | null;
};

/** Both animal-backed and sale-only lots have detail pages. */
function buildSaleLotNavTargets(items: SaleLotNavItem[]): SaleLotNavTarget[] {
  return items.map((item) => ({
    animalId:
      item.animalId != null &&
      Number.isInteger(item.animalId) &&
      item.animalId > 0
        ? item.animalId
        : null,
    lotNo: item.lotNo,
    lotAnimalId: item.lotAnimalId,
  }));
}

/** Keep the raw lot number: source data can contain meaningful padding. */
function matchesCurrentLot(
  item: SaleLotNavItem,
  animalId: string | null | undefined,
  lotNo: string,
): boolean {
  const hasAnimalId =
    Number.isInteger(Number(animalId)) && Number(animalId) > 0;
  return (
    item.lotNo === lotNo && (!hasAnimalId || String(item.animalId) === animalId)
  );
}

export function resolveSaleLotNavigationState(
  items: SaleLotNavItem[],
  animalId: string | null | undefined,
  lotNo: string,
  totalCount: number,
  directLot?: SaleLotNavItem | null,
) {
  const targets = buildSaleLotNavTargets(items);
  const currentIndex = targets.findIndex((target) =>
    matchesCurrentLot(target, animalId, lotNo),
  );
  // A direct detail query is independent of list filters and the navigation cap.
  // Do not show another lot's details when the requested lot is outside the list.
  const currentLot =
    directLot && matchesCurrentLot(directLot, animalId, lotNo)
      ? directLot
      : (items.find((item) => matchesCurrentLot(item, animalId, lotNo)) ??
        null);

  const prev = currentIndex > 0 ? (targets[currentIndex - 1] ?? null) : null;
  const next =
    currentIndex >= 0 && currentIndex < targets.length - 1
      ? (targets[currentIndex + 1] ?? null)
      : null;

  const canNavigate = currentIndex >= 0 && targets.length > 1;
  const positionLabel =
    currentIndex >= 0 && totalCount > 0
      ? `${currentIndex + 1} of ${totalCount}`
      : null;

  return {
    prev,
    next,
    currentLot,
    canNavigate,
    positionLabel,
    saleName: currentLot?.sale?.saleName ?? null,
  };
}
